import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback, useEffect, useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { DPE_STEPS } from '../../../../../@types/DPE';
import type { DpeTableData } from '../../../../../@types/DpeTableData';
import type { Owner } from '../../../../../@types/Owner';
import { setReloadPerf } from '../../../../../store/CoproReducer';
import { setReloadOwners } from '../../../../../store/DpeOwnerReducer';
import {
  resetAll,
  resetSelectedDpe,
  setCurrentStep,
  setIsDpeIdentified,
  setNbOwner,
  setOwners,
  setSelectedDpe,
} from '../../../../../store/DpeReducer';
import { useAppDispatch, useAppSelector } from '../../../../../store/hook';
import type { RootState } from '../../../../../store/store';
import classNames from '../../../../../utils/classNames';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import type { MUITableRow } from '../../../../common/MUITable';
import MUITable from '../../../../common/MUITable';
import { showToast } from '../../../../common/Notification';
import Spinner from '../../../../common/Spinner';
import { DPE_QUALIF_STEPS } from '../../../components/DpeStepsQualif/dpeQualifStep';
import DpeSteps from '../../../components/DpeStepsQualif/DpeStepsQualif.module';

interface DpeTableHeader {
  title: string;
  class?: string;
  customContentSelectedClass?: string;
}

export interface DpeTableRow {
  id: number;
  owners: Owner[];
  'Caractéristiques à identifier'?: React.ReactNode | { props?: { className?: string } };
  'Adresse IMMEUBLE'?: string | React.ReactNode;
  surface?: string;
  [key: string]: unknown;
}

interface DpeTableProps {
  data: DpeTableRow[];
  headers: DpeTableHeader[];
  tableClass?: string;
  noPaddingColumns?: string[];
  isDpeTreated?: boolean;
  isOldDpe?: boolean;
  // API Pagination props
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
}

const TableDPEContainer: React.FC<DpeTableProps> = ({
  data,
  headers,
  tableClass,
  noPaddingColumns,
  isDpeTreated = false,
  isOldDpe = false,
  // API Pagination props
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
}) => {
  const dispatch = useAppDispatch();
  const selectedDpe = useAppSelector((state) => state.dpe.selectedDpe);
  const isDpeIdentified = useSelector((state: RootState) => state.dpe.isDpeIdentified);
  const currentStep = useSelector((state: RootState) => state.dpe.currentStep);
  const [loading, setLoading] = useState<boolean>(false);
  const [treatingDpe, setTreatingDpe] = useState<boolean | number>(false);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const handleOwnerDetailClick = useCallback((item: DpeTableRow) => {
    if (selectedDpe && selectedDpe.id === item.id) {
      dispatch(resetSelectedDpe());
    } else {
      dispatch(setOwners(item.owners));
      dispatch(setNbOwner(item.owners.length));
      const { 'Caractéristiques à identifier': content, 'Adresse IMMEUBLE': contentAdress, ...sanitizedItem } = item;
      void content;
      void contentAdress;
      dispatch(setSelectedDpe(sanitizedItem as unknown as DpeTableData));
    }
  }, [dispatch, selectedDpe]);

  const handleDpeNextStep = useCallback(async(item: DpeTableRow) => {
    setLoading(true);
    setTreatingDpe(item.id);
    try {
      await fetch(`/api/dpes/${item.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: DPE_STEPS.IN_PROCESS }),
      });
      setLoading(false);
      setTreatingDpe(false);
      showToast('success', 'Le DPE est en cours de traitement.');
      dispatch(setIsDpeIdentified(true));
    } catch (error) {
      console.error('Error processing dpe :', error);
      showToast('error', 'Une erreur est survenue lors du traitement du DPE.');
    }
  }, [dispatch, jwt]);

  const handleOwnerClick = useCallback((item: DpeTableRow) => {
    const { 'Caractéristiques à identifier': content, 'Adresse IMMEUBLE': contentAdress, ...sanitizedItem } = item;
    void content;
    void contentAdress;
    if (selectedDpe && selectedDpe.id === item.id) {
      dispatch(resetAll());
    } else {
      dispatch(setSelectedDpe(sanitizedItem as unknown as DpeTableData));
    }
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.COMPLETING_OWNER_COUNT));
  }, [dispatch, selectedDpe]);

  useEffect(() => {
    if (isDpeIdentified) {
      dispatch(resetAll());
      dispatch(setReloadOwners(true));
      dispatch(setReloadPerf(true));
    }
  }, [isDpeIdentified, dispatch]);

  const extendedHeaders = useMemo(() => {
    const baseHeaders = headers.map((header) => {
      if (header.title === 'Caractéristiques à identifier') {
        return {
          ...header,
          class: selectedDpe ? ' bg-[#FFC000]' : '',
          customContentSelectedClass: selectedDpe ? ' bg-[#FEFF00]' : '',
        };
      }
      return header;
    });
    
    // Only add the Identifier column for non-old DPEs
    if (!isOldDpe) {
      baseHeaders.push({ title: 'Identifier', class: '' });
    }
    
    return baseHeaders;
  }, [headers, selectedDpe, isOldDpe]);

  const extendedData = useMemo(() => {
    const dataToShow = selectedDpe
      ? data.filter((item) => item.id === selectedDpe.id)
      : data;

    return dataToShow.map((item) => {
      const carac = item['Caractéristiques à identifier'] as { props?: { className?: string } } | undefined;
      const baseItem: Record<string, unknown> & { id: number; owners: Owner[] } = {
        ...item,
        'Caractéristiques à identifier': carac && typeof carac === 'object'
          ? { ...carac, props: { ...carac?.props, className: selectedDpe ? carac?.props?.className : '' } }
          : item['Caractéristiques à identifier'],
      };

      if (!isOldDpe) {
        baseItem.Identifier = (
          <>
            {(isDpeTreated) ? (
              <div className="flex justify-center">
                <button
                  className={classNames(
                    'w-full bg-rgpd-blue-dark text-white text-base rounded shadow-md align-center whitespace-nowrap py-3 px-5',
                    currentStep === DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED && 'opacity-60 cursor-not-allowed',
                  )}
                  onClick={() => currentStep !== DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED && handleOwnerClick(item)}
                >
                  {selectedDpe && selectedDpe.id === item.id ? (
                    <>
                      <ChevronDownIcon className="mr-2 inline h-5 w-5" />
                      Annuler l'identification
                    </>
                  ) : 'Ajouter un propriétaire'}
                </button>
              </div>
            ) : (
              currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS ? (
                <div className="justify-center">
                  <button
                    className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md"
                    onClick={() => handleOwnerDetailClick(item)}
                  >
                    {selectedDpe && selectedDpe.id === item.id ? 'Retour' : (
                      item.owners && item.owners.length > 1
                        ? `Détail des ${item.owners.length} propriétaires`
                        : 'Détail du propriétaire'
                    )}
                  </button>
                  <button
                    type="button"
                    className="align-center mt-5 w-full rounded bg-rgpd-green px-5 py-3 text-base whitespace-nowrap text-white shadow-md"
                    onClick={() => handleDpeNextStep(item)}
                  >
                    {loading && treatingDpe === item.id ? (
                      <>
                        <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                        Chargement...
                      </>
                    ) : 'Traiter le lead commercial'}
                  </button>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    className={classNames(
                      'w-full bg-rgpd-blue-dark text-white text-base rounded shadow-md align-center whitespace-nowrap py-3 px-5',
                      currentStep === DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED && 'opacity-60 cursor-not-allowed',
                    )}
                    onClick={() => currentStep !== DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED && handleOwnerClick(item)}
                  >
                    {selectedDpe && selectedDpe.id === item.id ? (
                      <>
                        <ChevronDownIcon className="mr-2 inline h-5 w-5" />
                        Annuler l'identification
                      </>
                    ) : 'Trouver le propriétaire'}
                  </button>
                </div>
              )
            )}
          </>
        );
      }

      return baseItem;
    });
  }, [data, isDpeTreated, currentStep, selectedDpe, loading, treatingDpe, isOldDpe, handleOwnerDetailClick, handleDpeNextStep, handleOwnerClick]);

  return (
    <>
      <MUITable
        headers={extendedHeaders}
        data={extendedData as MUITableRow[]}
        tableClass={tableClass}
        noPaddingColumns={noPaddingColumns}
        disablePagination={selectedDpe != null}
        isSearchable={false}
        apiPagination={apiPagination}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
      />
      {selectedDpe && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 1, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-4"
          >
            <DpeSteps
              isDpeTreated={isDpeTreated}
              area={isNaN(parseFloat(selectedDpe.surface)) ? 0 : parseFloat(selectedDpe.surface)}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
};

export default TableDPEContainer;
